import { Box, Divider, Skeleton, Stack, Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/system';
import { FC } from 'react';
import { CfCheckboxList } from '../../../../../cf-ui/CheckboxList/CfCheckboxList';
import { CfCheckbox, CheckboxItem } from '../../../../../cf-ui/CheckboxList/components/Checkbox/CfCheckbox';
import { DeviceType, useBreakpoint } from '../../../../../hooks/useBreakpoint';
import UserCheckboxLabel, { UserCheckboxItem } from './components/UserCheckboxLabel';
import { StyledContainer, StyledWrapper } from './UserCheckboxList.styles';

export interface UserCheckboxListProps {
  items: UserCheckboxItem[];
  onChange?: (items: UserCheckboxItem[]) => void;
  isLoading?: boolean;
  sx?: SxProps<Theme>;
  greyBackground?: boolean;
  dataTestId?: string;
  dataAltTestId?: string;
  gap?: number | string | ((theme: Theme) => string | number);
}

const selectAllValue = 'select-all-user-checkboxes';

const UserCheckboxList: FC<UserCheckboxListProps> = (props: UserCheckboxListProps) => {
  const { items, onChange, isLoading, dataTestId, dataAltTestId, gap } = props;
  const { deviceType } = useBreakpoint();

  const checkedItemsCount = items.filter((i) => i.selected).length;
  const areAllSelected = checkedItemsCount === items.length;
  const selectAllIcon = areAllSelected ? 'check' : 'minus';

  const handleSelectAll = (item: CheckboxItem) => {
    const newOptions = items.map((o) => {
      return { ...o, selected: o.disabled ? o.selected : item.checked };
    });
    onChange?.(newOptions);
  };

  const handleOnCheckedChange = (item: CheckboxItem) => {
    if (item.additionalValue === selectAllValue) return handleSelectAll(item);

    const optionIndex = items.findIndex((o) => o.userId === item.additionalValue);
    if (optionIndex >= 0) {
      const option = items[optionIndex];

      const newOptions = [...items];
      newOptions[optionIndex] = { ...option, selected: !option.selected };

      onChange?.(newOptions);
    }
  };

  const checkboxItems = items.map((option) => {
    return {
      label: <UserCheckboxLabel item={option} />,
      checked: option.selected,
      additionalValue: option.userId,
      disabled: option.disabled,
    } as CheckboxItem;
  });

  const selectAll = {
    label: (
      <Typography variant='responsiveParagraph' color='grey.700'>
        Select all
      </Typography>
    ),
    checked: checkedItemsCount > 0,
    name: 'Select all',
    additionalValue: selectAllValue,
  } as CheckboxItem;

  const greyBackground = props.greyBackground !== false;
  return (
    <StyledWrapper greyBackground={greyBackground} sx={props.sx}>
      {isLoading ? (
        <StyledContainer greyBackground={greyBackground}>
          {[1, 2, 3, 4].map((i) => (
            <UserCheckboxLoadingSkeleton key={i} />
          ))}
        </StyledContainer>
      ) : (
        <>
          {checkboxItems.length > 1 && (
            <>
              <StyledContainer greyBackground={greyBackground} sx={{ height: 42 }}>
                <CfCheckbox
                  dataTestId={dataTestId}
                  item={selectAll}
                  onCheckedChanged={handleSelectAll}
                  icon={selectAllIcon}
                  size={deviceType === DeviceType.Desktop ? 'small' : 'medium'}
                />
              </StyledContainer>
              <Divider sx={{ borderColor: (theme: Theme) => theme.palette.common.white }} />
            </>
          )}
          <StyledContainer greyBackground={greyBackground}>
            <CfCheckboxList
              items={checkboxItems}
              length={checkboxItems.length}
              onCheckedChange={handleOnCheckedChange}
              variant='primary'
              icon='check'
              dataTestId='user-checkbox-input'
              dataAltTestId={dataAltTestId}
              checkboxSize={deviceType === DeviceType.Desktop ? 'small' : 'medium'}
              gap={gap}
            />
            {items.length === 0 && (
              <Box display='flex' alignItems='center' justifyContent='center'>
                <Typography variant='paragraph'>No results found</Typography>
              </Box>
            )}
          </StyledContainer>
        </>
      )}
    </StyledWrapper>
  );
};

export default UserCheckboxList;

const UserCheckboxLoadingSkeleton = (): JSX.Element => {
  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction='row' sx={{ height: 18 }}>
        <Skeleton width={16} height={16} variant='rectangular' />
        <Typography variant='responsiveParagraphBold' width='70%'>
          <Skeleton sx={{ ml: 1 }} variant='rectangular' height={16} />
        </Typography>
      </Stack>
      <Typography variant='responsiveParagraph' width='40%' component='div'>
        <Skeleton sx={{ ml: 3, mb: 0.5 }} variant='rectangular' height={14} />
      </Typography>
    </Box>
  );
};
